/**
 * The tenant scope — story 0.5 AC-3, AC-7, AC-8.
 *
 * 🔒 **AC-8 asks for a STRUCTURAL guard, not an `if` in every screen.** This is a
 * layout route at `/cliente/:idCliente`, so a scoped screen is rendered as its
 * child or not at all. There is no code path that reaches a FinOps tab without a
 * tenant in scope, which is why `useClienteAtual()` can return a plain `string`
 * instead of `string | null` and no screen ever writes a null check.
 *
 * ⚠️ AC-7: an `idCliente` the user does not hold renders an access-denied state and
 * **fires no request**. Not because the SPA is a security boundary — BR-17 is
 * explicit that it is not — but because sending a request we know will be refused
 * produces a screen full of errors instead of a sentence explaining the problem.
 */
import { useEffect } from 'react'
import { Navigate, Outlet, useParams } from 'react-router'

import { useSession } from '@/auth/session'
import { podeAcessar } from '@/tenant/clientes'
import { lembrarCliente } from '@/tenant/persistencia'
import { ContextoDeCliente } from '@/tenant/useClienteAtual'

function AcessoNegado({ idCliente }: { idCliente: string }) {
  return (
    <section className="mx-auto max-w-lg py-16 text-center" role="alert">
      <i className="ph ph-lock-simple text-iox-navy mb-4 block text-4xl" aria-hidden="true" />
      <h1 className="type-h2 text-iox-navy mb-2">Você não tem acesso a este cliente</h1>
      <p className="type-body text-navy-600">
        O cliente <code className="type-label">{idCliente}</code> não está na sua lista de acesso.
        Se isso estiver errado, fale com um administrador da plataforma.
      </p>
    </section>
  )
}

export function TenantScope() {
  const { idCliente } = useParams()
  const { sessao } = useSession()
  const permitidos = sessao?.clientes ?? []
  const autorizado = Boolean(idCliente) && podeAcessar(idCliente!, permitidos)

  useEffect(() => {
    // Persist only what the user actually reached — remembering a tenant we then
    // denied would hand the next visit the same dead end (AC-4).
    if (autorizado && idCliente) lembrarCliente(idCliente)
  }, [autorizado, idCliente])

  // The route cannot match without the param, but the guard states the invariant
  // rather than trusting the router to keep it.
  if (!idCliente) return <Navigate to="/" replace />

  if (!autorizado) {
    // Deliberately NOT a redirect: bouncing to another tenant would hide the fact
    // that the link was wrong, and the user would wonder why the numbers changed.
    return <AcessoNegado idCliente={idCliente} />
  }

  return (
    <ContextoDeCliente.Provider value={idCliente}>
      <Outlet />
    </ContextoDeCliente.Provider>
  )
}
